import React, { useEffect, useState } from 'react';
import useFetch from '../../../api/useFetch';
import { updateOrCreate } from '../../special/updateOrCreate';
import { NewMatchingProperty } from '../../new/NewMatchingProperty';
import {
  Box,
  Button,
  colors,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  useThemeProps,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ShortTextIcon from '@mui/icons-material/ShortText';
import NumbersIcon from '@mui/icons-material/Numbers';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { tokens } from '../../../global/theme/tokens';
import PublicIcon from '@mui/icons-material/Public';
import ExoDialog from '../ExoDialog';
import { ProjectAddElementButton } from '../project/ProjectAddElementButton';
import { t } from 'i18next';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { API_POST, DBRequest, useConfig } from '../../../api/api';
import Loader from '../../special/Loader';
import { ExoSkeleton } from '../ExoSkeleton';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import Pagination from '../../special/Pagination';

export const MatchingPropertiesList = ({
  properties,
  isEditMode,
  salesLevelId,
  salesObjectId,
  onSelectionChange,
  onFeedback,
  onClose,
  onPropChange,
  noGlobalList,
  objectProperties,
  noValueEdit,
  isLead,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [selection, setSelection] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (noGlobalList) return;
    DBRequest({
      config,
      path: `sales-object-properties?isGlobal[eq]=1`,
      onResponse: setApiData,
    });
  }, [config]);

  useEffect(() => {
    setSelection(properties || []);
  }, [properties]);

  function handlePropChange(newProp) {
    if (newProp.data.isGlobal && !noGlobalList) {
      // change db collection
      const newProps = updateOrCreate(apiData, newProp.data);
      setApiData(newProps);
      if (newProp.length > apiData.data.length)
        handleSelectionChange(newProp.data);
    } else {
      // change props in object
      onPropChange(newProp);
    }
  }

  function handleSelectionChange(element) {
    const exists = selection.findIndex((prop) => prop.id === element.id) !== -1;
    if (exists) {
      //remove from arr
      const newSelection = selection.filter((prop) => prop.id !== element.id);
      setSelection(newSelection);
    } else {
      //add to arr
      const newSelection = [...selection, element];
      setSelection(newSelection);
    }
  }

  const iconMap = {
    text: ShortTextIcon,
    number: NumbersIcon,
    checkbox: CheckBoxIcon,
    select: FormatListBulletedIcon,
  };

  function syncProperties() {
    if (isLead)
      return onSelectionChange(selection, salesObjectId, salesLevelId);

    var relations = [];
    selection.forEach((element) => {
      relations.push(element.id);
    });

    const reqData = {
      relations,
      mode: 'sync',
    };

    DBRequest({
      config,
      path: salesLevelId
        ? `sales-object-levels/${salesLevelId}/relations`
        : `sales-objects/${salesObjectId}/relations`,
      method: API_POST(reqData),
      onLoading: setIsLoading,
      onResponse: () => {
        onClose();
        onSelectionChange(selection, salesObjectId, salesLevelId);
      },
    });
  }

  function handleClose() {
    setOpen(false);
    setEdit(null);
  }

  //Handle State after Delete (Properties from the prospect matching)
  function handleDelete(deletedProp) {
    if (deletedProp.isGlobal && !noGlobalList) {
      const updatedProps = apiData?.data?.filter(
        (prop) => prop.id !== deletedProp.id
      );
      setApiData({ ...apiData, data: updatedProps });
    }
  }

  return (
    <Box className="flex flex-col gap-1">
      <Loader active={isLoading} />
      <Typography variant="h4" className="pb-3">
        {t('Select properties')}
      </Typography>
      <Box
        className={
          'grid  gap-2 ' +
          (!noGlobalList && objectProperties && 'md:grid-cols-2')
        }
      >
        {objectProperties && (
          <Box
            className="flex flex-col gap-1 px-3 py-2 rounded-lg"
            sx={{ bgcolor: colors.card }}
          >
            <Typography variant="h5" fontWeight={500}>
              {t('Properties from the sales object')}
            </Typography>
            {objectProperties?.map((property) => (
              <PropPreview
                key={property.id}
                property={property}
                onSelect={handleSelectionChange}
                isSelected={selection.find((obj) => obj.id === property.id)}
                Icon={iconMap[property.type]}
                noGlobalList={noGlobalList}
                isEditMode={isEditMode && !noValueEdit}
                onEdit={(prop) => {
                  setOpen(true);
                  setEdit(prop);
                }}
              />
            ))}
          </Box>
        )}

        {!noGlobalList && (
          <Box
            className="flex flex-col gap-1 px-3 py-2 rounded-lg"
            sx={{ bgcolor: colors.card }}
          >
            <Typography variant="h5" fontWeight={500}>
              {t('Properties from the prospect matching')}
            </Typography>
            {apiData ? (
              apiData?.data?.map((property) => (
                <PropPreview
                  property={property}
                  onSelect={handleSelectionChange}
                  isSelected={selection.find((obj) => obj.id === property.id)}
                  Icon={iconMap[property.type]}
                  onEdit={(prop) => {
                    setOpen(true);
                    setEdit(prop);
                  }}
                  isEditMode={isEditMode && !noValueEdit}
                  noGlobalList={true}
                />
              ))
            ) : (
              <ExoSkeleton sizePx="37px" n={3} />
            )}
            <Pagination
              data={apiData}
              setData={setApiData}
              setLoading={setIsLoading}
              isSmall
            />
          </Box>
        )}
      </Box>
      {isEditMode && !noValueEdit && (
        <ProjectAddElementButton sizePx="30px" onClick={() => setOpen(true)} />
      )}
      <Box className="w-full flex justify-between pt-4">
        <Button variant="outlined" color="error" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button variant="contained" color="success" onClick={syncProperties}>
          {t('Save')}
        </Button>
      </Box>
      <ExoDialog limitWidth open={open} onClose={handleClose}>
        <NewMatchingProperty
          startData={edit}
          startType={edit?.type || 'text'}
          salesLevelId={salesLevelId}
          onClose={handleClose}
          onUpdate={handlePropChange}
          onCreate={handlePropChange}
          onFeedback={onFeedback}
          salesObjectId={salesObjectId}
          onDelete={handleDelete}
        />
      </ExoDialog>
    </Box>
  );
};

const PropPreview = ({
  property,
  onSelect,
  isSelected,
  Icon,
  onEdit,
  isEditMode,
  noGlobalList,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { label, isGlobal, isShareable } = property;

  if (!noGlobalList && isGlobal) return;
  return (
    <Box className="flex w-full gap-2">
      <Button
        sx={{
          padding: '5px 10px',
          textAlign: 'start',
          justifyContent: 'start',
          gap: '5px',
          border: `1px solid transparent`,
          ':hover': {
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
          },
        }}
        className="w-full"
        onClick={() => onSelect(property)}
      >
        {isSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
        {label}
        <Box className="ml-auto flex items-center">
          <Icon />
          {isGlobal && (
            <Tooltip title={t('is used for matching interested parties')}>
              <PublicIcon sx={{ color: colors.success }} className="ml-2" />
            </Tooltip>
          )}
          {isShareable && (
            <Tooltip title={t('Will be shared with seller.')}>
              <CoPresentIcon sx={{ color: colors.warning }} className="ml-2" />
            </Tooltip>
          )}
        </Box>
      </Button>
      {isEditMode && (
        <IconButton onClick={() => onEdit(property)}>
          <EditIcon />
        </IconButton>
      )}
    </Box>
  );
};
